import { Box, Button, Chip, ListItemText, Menu, MenuItem, SvgIcon, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { IPublication } from '@opengov/stories-shared/types';
import { FileEditOutline } from '@opengov/react-capital-assets';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  IExtendedStoryPrivilege,
  IPublicationModalMessages,
  IPublicationViewMessages,
  ISessionUser,
  ISettings,
  IStoriesFolder,
  IStoryListMessages,
  ISubjectClaims,
  IsubjectClaimsMessages
} from '../../../../../types';

import { fetchAllPublicationsInEntity } from '../../../StoryList/hooks/fetchAllPublicationsInEntity';
import {
  navigateToCreateFromTemplateInPublication,
  navigateToPublicationCustomCssView,
  navigateToPublicationView
} from '../../../../state/navigation';
import { CreateStoryModal } from '../../../StoryList/CreateStoryModal/CreateStoryModal';

import { CopyPublicationModal } from '../../CopyPublicationModal/CopyPublicationModal';
import { Header } from '../../Header/Header';

import { styles } from './PublicationViewHeader.styles';
import { PUBLICATION_GRANULAR_ACCESS } from '../../../../constants';
import { PUBLICATIONS_MANAGER_PRIVILEGE, STORIES_SUPER_ADMIN_PRIVILEGE } from '@opengov/stories-shared/constants';
import { hasPrivilege } from '../../../../utils';

export type IPublicationsHeaderProps = {
  messages: IPublicationViewMessages;
  publicationModalMessages: IPublicationModalMessages;
  storyMessages: IStoryListMessages;
  title: string;
  isAdmin: boolean;
  canOrganizeStories?: boolean;
  currentFolderId?: string;
  isCreateFromTemplate: boolean;
  publicationFolderStructure: IStoriesFolder[];
  isPublicationCustomCssEnabled: boolean;
  isPublicationSetupPdfEnabled: boolean;
  isBulkUpdateStoryStatusEnabled: boolean;
  copyPublicationJobId: string;
  user: ISessionUser;
  entityName: string;
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
  publication: IPublication;
  settings: ISettings;
  isExportDisabled: boolean;
  totalStoriesCount: number;
  storiesAccessControlEnabled: boolean;
  subjectClaimsMessages: IsubjectClaimsMessages;
  currentSubjectClaims?: ISubjectClaims | null;
  currentSubjectPublicationStoryClaim: IExtendedStoryPrivilege | null;
  openModal(modal: string): IAction<string, string>;
  setPage(value: number): void;
  setIsDeleteModalVisible(value: boolean): void;
  navigateToPublicationList(): any;
  updateThumbnail(publicationId: string, controller?: AbortController): void;
  setIsPublicationExportPdfModalVisible(value: boolean): void;
  navigateToOrganizeSectionsAndStories(): void;
  navigateToSetupOnlinePublication(): void;
  canCreateStories?: boolean;
  navigateToPublicationSetupPDF(publicationId: string): void;
};

export const PublicationViewHeader: React.FC<IPublicationsHeaderProps> = (props) => {
  const {
    isAdmin,
    currentFolderId,
    publicationFolderStructure,
    messages,
    entityName,
    entityId,
    entityUuid,
    entitySubdomain,
    user,
    isCreateFromTemplate = false,
    title,
    storyMessages,
    publicationModalMessages,
    publication,
    canOrganizeStories,
    settings,
    isExportDisabled,
    totalStoriesCount,
    isPublicationCustomCssEnabled,
    isPublicationSetupPdfEnabled,
    isBulkUpdateStoryStatusEnabled,
    storiesAccessControlEnabled,
    copyPublicationJobId,
    subjectClaimsMessages,
    currentSubjectClaims,
    currentSubjectPublicationStoryClaim,
    openModal,
    setPage,
    updateThumbnail,
    setIsDeleteModalVisible,
    navigateToPublicationList,
    setIsPublicationExportPdfModalVisible,
    navigateToOrganizeSectionsAndStories,
    navigateToSetupOnlinePublication,
    canCreateStories,
    navigateToPublicationSetupPDF
  } = props;

  const [isCreateStoryModalVisible, setCreateStoryModalVisible] = useState(false);
  const [isCopyPublicationModalVisible, setCopyPublicationModalVisible] = useState(false);
  const [manageAnchorEl, setManageAnchorEl] = useState<null | HTMLElement>(null);
  const [addStoryAnchorEl, setAddStoryAnchorEl] = useState<null | HTMLElement>(null);
  const [allPublicationsTitles, setAllPublicationsTitles] = useState([]);
  const publicationsListControllerRef = useRef(null);

  const getPublicationsList = debounce(
    () => {
      if (publicationsListControllerRef.current) {
        publicationsListControllerRef.current.abort();
      }
      const controller = new AbortController();
      publicationsListControllerRef.current = controller;

      void fetchAllPublicationsInEntity(entityId, publicationsListControllerRef.current).then(
        (response) => {
          if (Array.isArray(response)) {
            setAllPublicationsTitles(response.map((pub) => pub.title));
          }
        }
      );
    },
    100,
    { trailing: true }
  );

  useEffect(() => {
    if (entityId && !allPublicationsTitles.length) {
      getPublicationsList();
    }
  }, [entityId]);

  const isPublicationEmpty = totalStoriesCount < 1;
  const isStoriesSuperAdmin = hasPrivilege(currentSubjectClaims ?? null, STORIES_SUPER_ADMIN_PRIVILEGE);
  const showCustomCss = isPublicationCustomCssEnabled || isStoriesSuperAdmin;

  const renderCustomCssDropdownItem = () => (
    <MenuItem
      disabled={isPublicationEmpty}
      data-test="publication-custom-css"
      onClick={() => { setManageAnchorEl(null); navigateToPublicationCustomCssView(entitySubdomain, publication.id); }}
    >
      {messages.publicationCustomCss}
      <Chip label={messages.publicationCustomCssBadge} size="small" color="primary" sx={styles.customCssBadge} />
    </MenuItem>
  );

  const handleOpenDialog = () => {
    openModal(PUBLICATION_GRANULAR_ACCESS);
  };

  const renderManageButton = () => {
    return (
      <>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          data-test="manage-publication-dropdown"
          onClick={(e) => setManageAnchorEl(e.currentTarget)}
          endIcon={<ArrowDropDownIcon aria-hidden="true" />}
        >
          {messages.manage}
        </Button>
        <Menu
          anchorEl={manageAnchorEl}
          open={Boolean(manageAnchorEl)}
          onClose={() => setManageAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {currentSubjectPublicationStoryClaim?.includes(PUBLICATIONS_MANAGER_PRIVILEGE) && (
            <MenuItem
              data-test="publication-copy"
              onClick={() => { setManageAnchorEl(null); setCopyPublicationModalVisible(true); }}
            >
              {messages.copyPublication}
            </MenuItem>
          )}
          {storiesAccessControlEnabled && isAdmin && (
            <MenuItem
              data-test="manage-access"
              onClick={() => { setManageAnchorEl(null); handleOpenDialog(); }}
            >
              {subjectClaimsMessages.manageAccess}
            </MenuItem>
          )}
          {publicationFolderStructure.length ? (
            <MenuItem
              data-test="publication-organization-and-navigation"
              onClick={() => { setManageAnchorEl(null); navigateToOrganizeSectionsAndStories(); }}
            >
              {messages.organizePdf}
            </MenuItem>
          ) : (
            <Tooltip title="No sections or stories available in the publication to organize." placement="bottom-end">
              <Box component="span">
                <MenuItem
                  data-test="publication-organization-and-navigation"
                  disabled
                >
                  {messages.organizePdf}
                </MenuItem>
              </Box>
            </Tooltip>
          )}
          {currentSubjectPublicationStoryClaim?.includes(PUBLICATIONS_MANAGER_PRIVILEGE) && (
            <MenuItem
              data-test="publication-setup-online-publication"
              onClick={() => { setManageAnchorEl(null); navigateToSetupOnlinePublication(); }}
            >
              {messages.setupOnlinePublication}
            </MenuItem>
          )}
          {showCustomCss && (
            isPublicationEmpty ? (
              <Tooltip title={messages.publicationCustomCssDisabledTooltip} placement="bottom-end">
                <Box component="span">
                  {renderCustomCssDropdownItem()}
                </Box>
              </Tooltip>
            ) : (
              renderCustomCssDropdownItem()
            )
          )}
          {currentSubjectPublicationStoryClaim?.includes(PUBLICATIONS_MANAGER_PRIVILEGE) && (
            <MenuItem
              data-test="publication-delete"
              sx={styles.deleteMenuItem}
              onClick={() => { setManageAnchorEl(null); setIsDeleteModalVisible(true); }}
            >
              {messages.delete}
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  if (isCreateStoryModalVisible) {
    return createPortal(
      <CreateStoryModal
        isAdmin={isAdmin || canCreateStories}
        canOrganizeStories={canOrganizeStories}
        currentFolderId={currentFolderId}
        folderStructure={publicationFolderStructure}
        messages={storyMessages}
        entityName={entityName}
        entityId={entityId}
        entitySubdomain={entitySubdomain}
        user={user}
        publication={publication}
        onClose={() => setCreateStoryModalVisible(false)}
        settings={settings}
        updateThumbnail={updateThumbnail}
      />,
      document.getElementById('app')
    );
  }

  if (isCopyPublicationModalVisible) {
    return createPortal(
      <CopyPublicationModal
        isAdmin={isAdmin}
        isBulkUpdateStoryStatusEnabled={isBulkUpdateStoryStatusEnabled}
        user={user}
        messages={publicationModalMessages}
        entityId={entityId}
        entityUuid={entityUuid}
        entityName={entityName}
        entitySubdomain={entitySubdomain}
        allPublicationsTitles={allPublicationsTitles}
        sourcePublication={publication}
        currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim}
        onClose={() => setCopyPublicationModalVisible(false)}
      />,
      document.getElementById('app')
    );
  }

  const renderAddNewStoryButton = () => {
    return (
      <>
        <Button
          variant="contained"
          size="small"
          data-test="publication-add-new-story-dropdown"
          onClick={(e) => setAddStoryAnchorEl(e.currentTarget)}
          endIcon={<ArrowDropDownIcon aria-hidden="true" />}
        >
          {messages.addNewStory}
        </Button>
        <Menu
          anchorEl={addStoryAnchorEl}
          open={Boolean(addStoryAnchorEl)}
          onClose={() => setAddStoryAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            data-test="publication-create-from-scratch"
            onClick={() => { setAddStoryAnchorEl(null); setCreateStoryModalVisible(true); }}
          >
            <ListItemText
              primary={messages.createFromScratch}
              secondary={messages.createFromScratchDescription}
            />
          </MenuItem>
          {currentSubjectPublicationStoryClaim?.includes(PUBLICATIONS_MANAGER_PRIVILEGE) && (
            <MenuItem
              data-test="publication-create-from-template"
              onClick={() => {
                setAddStoryAnchorEl(null);
                setPage(1);
                navigateToCreateFromTemplateInPublication(entitySubdomain, publication.id);
              }}
            >
              <ListItemText
                primary={messages.createFromTemplate}
                secondary={messages.createFromTemplateDescription}
              />
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const renderExportPdfButton = () => {
    return (
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        data-test="publication-export-pdf"
        onClick={() => setIsPublicationExportPdfModalVisible(true)}
        disabled={isExportDisabled || !totalStoriesCount}
        startIcon={<FileDownloadOutlinedIcon aria-hidden={true} />}
      >
        {messages.pdfExport}
      </Button>
    );
  };

  const renderSetupPdfButton = () => {
    return (
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        data-test="publication-setup-pdf"
        onClick={() => navigateToPublicationSetupPDF(publication.id)}
        disabled={isExportDisabled || !totalStoriesCount}
        startIcon={<SvgIcon aria-hidden={true} component={FileEditOutline} inheritViewBox fontSize="small" />}
      >
        {messages.setupPdf}
      </Button>
    );
  };

  const renderCancelButton = () => {
    return (
      <Button
        variant="outlined"
        size="small"
        data-test="cancel-select-template"
        onClick={() => {
          setPage(1);
          navigateToPublicationView(entitySubdomain, publication.id);
        }}
      >
        {messages.cancel}
      </Button>
    );
  };
  return (
    <Box data-test="publication-header-container">
      <Header
        copyPublicationJobId={copyPublicationJobId}
        breadcrumbTitle={title}
        messages={messages}
        publicationModalMessages={publicationModalMessages}
        navigateToPublicationList={navigateToPublicationList}
        isAdmin={isAdmin}
        publication={publication}
        entityId={entityId}
        userId={user.id}
        isCreateFromTemplate={isCreateFromTemplate}
      >
        {!isCreateFromTemplate ? (
          <>
            {!copyPublicationJobId && isPublicationSetupPdfEnabled && isAdmin && renderSetupPdfButton()}
            {!copyPublicationJobId && !isPublicationSetupPdfEnabled && isAdmin && renderExportPdfButton()}
            {!copyPublicationJobId && isAdmin && renderManageButton()}
            {!copyPublicationJobId &&
              (isAdmin || canCreateStories) &&
              publicationFolderStructure.length > 0 &&
              renderAddNewStoryButton()}
          </>
        ) : (
          renderCancelButton()
        )}
      </Header>
    </Box>
  );
};
